<script setup lang="ts">
import { computed, ref } from 'vue';
import LabelPreview from './LabelPreview.vue';
import type { ToolStatus } from '../lib/useChat';

const props = defineProps<{
  name: string;
  status: ToolStatus;
  text: string;
  shipmentIds: number[];
}>();

const expanded = ref(false);

/** Plain-language labels; the tool name itself is jargon to a merchant. */
const LABELS: Record<string, string> = {
  get_carrier_capabilities: 'Vervoerders opgezocht',
  get_delivery_options: 'Bezorgopties opgehaald',
  create_shipment: 'Zending aangemaakt',
  get_shipment_label: 'Label opgehaald',
  track_shipment: 'Zending getraceerd',
  create_return_shipment: 'Retourlabel aangemaakt',
};

const RUNNING_LABELS: Record<string, string> = {
  get_carrier_capabilities: 'Vervoerders opzoeken…',
  get_delivery_options: 'Bezorgopties ophalen…',
  create_shipment: 'Zending aanmaken…',
  get_shipment_label: 'Label ophalen…',
  track_shipment: 'Zending traceren…',
  create_return_shipment: 'Retourlabel aanmaken…',
};

const headline = computed(() => {
  if (props.status === 'running') {
    return RUNNING_LABELS[props.name] ?? `${props.name}…`;
  }
  if (props.status === 'declined') {
    return 'Actie geannuleerd';
  }
  if (props.status === 'error') {
    return `Mislukt: ${LABELS[props.name] ?? props.name}`;
  }
  return LABELS[props.name] ?? props.name;
});
</script>

<template>
  <div class="tool" :class="`tool--${status}`">
    <button class="tool__summary" type="button" @click="expanded = !expanded">
      <span class="tool__dot" aria-hidden="true"></span>
      <span class="tool__label">{{ headline }}</span>
      <span v-if="status !== 'running'" class="tool__chevron" aria-hidden="true">
        {{ expanded ? '−' : '+' }}
      </span>
    </button>

    <pre v-if="expanded && text" class="tool__detail">{{ text }}</pre>

    <LabelPreview v-if="shipmentIds.length > 0" :shipment-ids="shipmentIds" />
  </div>
</template>

<style scoped>
.tool {
  border: 1px solid var(--border);
  border-radius: 10px;
  margin: 8px 0;
  background: var(--surface-muted);
  overflow: hidden;
}

.tool__summary {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: none;
  border: none;
  font: inherit;
  font-size: 13px;
  color: var(--text);
  cursor: pointer;
  text-align: left;
}

.tool__label {
  flex: 1;
}

.tool__chevron {
  color: var(--text-muted);
  font-size: 15px;
}

.tool__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: var(--text-muted);
}

.tool--running .tool__dot {
  background: var(--accent);
  animation: pulse 1.2s ease-in-out infinite;
}

.tool--ok .tool__dot {
  background: var(--accent);
}

.tool--error .tool__dot {
  background: var(--danger);
}

.tool--error {
  border-color: var(--danger-border);
}

.tool__detail {
  margin: 0;
  padding: 10px 12px;
  border-top: 1px solid var(--border);
  background: var(--code-bg);
  font-size: 12px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tool--running .tool__dot {
    animation: none;
  }
}
</style>
